import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A "Buy Now" button that reuses the real add-to-cart button's state (variant
 * sync, quantity rules, disabled/sold-out) by clicking it programmatically,
 * then redirects to checkout once the add succeeds — rather than duplicating
 * any of that validation/sync logic itself. Modeled on
 * sticky-add-to-cart.js's handleAddToCartClick pattern.
 *
 * @extends {Component<{ button: HTMLButtonElement }>}
 */
class BuyNowButtonComponent extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleAddFailed, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @type {boolean} */
  #awaitingRedirect = false;

  handleClick = () => {
    const targetAddToCartButton = this.#getAddToCartButton();
    if (!targetAddToCartButton || targetAddToCartButton.disabled) return;

    this.#awaitingRedirect = true;
    this.refs.button.disabled = true;
    targetAddToCartButton.click();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleAddComplete = (event) => {
    if (!this.#awaitingRedirect) return;
    this.#awaitingRedirect = false;

    event.promise
      .then(() => {
        window.location.href = '/checkout';
      })
      .catch(() => {
        this.refs.button.disabled = false;
      });
  };

  #handleAddFailed = () => {
    if (!this.#awaitingRedirect) return;
    this.#awaitingRedirect = false;
    this.refs.button.disabled = false;
  };

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getAddToCartButton() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const productForm = sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
    return productForm?.querySelector('[ref="addToCartButton"]') ?? null;
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButtonComponent);
}
